/**
 * Super Admin — Users management.
 * - Summary tiles
 * - Search + filter + sort
 * - Add user modal
 * - Batch FitPoints modal
 * - User detail drawer with tabs (Profile, FitPoints, Actions)
 */
import { useEffect, useState, useCallback } from "react";
import { Search, UserPlus, Coins, X } from "lucide-react";
import { apiClient } from "../../services/api";
import { toast } from "sonner";
import { SectionHeader } from "./SuperadminApp";
import {
  card, inputStyle, fieldLabel, PrimaryBtn, GhostBtn, DangerBtn,
  StatusBadge, Modal, FilterPills, SummaryTiles, Card, TableShell, Th, Td, useDebouncedValue,
} from "./_atoms";

const STATUSES = [
  { value: "", label: "All" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "professional", label: "Professional" },
  { value: "banned", label: "Banned" },
];

const SORTS = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
  { value: "active", label: "Most active" },
  { value: "fitpoints", label: "Most FitPoints" },
  { value: "spend", label: "Highest spend" },
];

export default function SuperadminUsers() {
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const dq = useDebouncedValue(q, 350);
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("newest");
  const [openAdd, setOpenAdd] = useState(false);
  const [openBatch, setOpenBatch] = useState(false);
  const [drawerUid, setDrawerUid] = useState(null);

  const load = useCallback(() => {
    apiClient.get("/superadmin/users", {
      params: { q: dq || undefined, status: status || undefined, sort, page: 1, size: 50 },
    }).then((r) => { setUsers(r.data.users || []); setTotal(r.data.total || 0); });
  }, [dq, status, sort]);

  useEffect(() => {
    apiClient.get("/superadmin/users/summary").then((r) => setSummary(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="superadmin-users">
      <SummaryTiles tiles={[
        { label: "Total registered", value: summary?.total_registered },
        { label: "Verified pros", value: summary?.verified_professionals },
        { label: "Guests today", value: summary?.guest_sessions_today },
        { label: "Banned", value: summary?.banned },
      ]} />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <SectionHeader title="All Users" />
        <div style={{ display: "flex", gap: 8 }}>
          <GhostBtn onClick={() => setOpenBatch(true)} data-testid="open-batch-fp">
            <Coins size={14} style={{ marginRight: 6, verticalAlign: "-2px" }} /> Batch FitPoints
          </GhostBtn>
          <PrimaryBtn onClick={() => setOpenAdd(true)} data-testid="open-add-user">
            <UserPlus size={14} style={{ marginRight: 6, verticalAlign: "-2px" }} /> Add User
          </PrimaryBtn>
        </div>
      </div>

      <Card style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: "1 1 280px" }}>
          <Search size={14} style={{ position: "absolute", top: 15, left: 14, color: "var(--text-tertiary, #8A8884)" }} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, phone…"
            data-testid="user-search"
            style={{ ...inputStyle, paddingLeft: 36 }}
          />
        </div>
        <FilterPills value={status} onChange={setStatus} options={STATUSES} testid="user-status-filter" />
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          style={{ ...inputStyle, width: 180 }}
          data-testid="user-sort"
        >
          {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </Card>

      <TableShell>
        <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="users-table">
          <thead>
            <tr>
              <Th>User</Th><Th>Email</Th><Th>Phone</Th>
              <Th>Registered</Th><Th>Last active</Th>
              <Th>FitPoints</Th><Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr
                key={u.user_id}
                onClick={() => setDrawerUid(u.user_id)}
                style={{ cursor: "pointer" }}
                data-testid={`user-row-${u.user_id}`}
              >
                <Td>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Avatar name={u.name} url={u.avatar_url} />
                    <span style={{ color: "var(--text-primary, #0C0C0B)", fontWeight: 600 }}>{u.name}</span>
                  </div>
                </Td>
                <Td>{u.email}</Td>
                <Td>{u.phone || "—"}</Td>
                <Td>{(u.created_at || "").slice(0, 10)}</Td>
                <Td>{relTime(u.last_active_at)}</Td>
                <Td>{u.fitpoints_balance ?? 0}</Td>
                <Td>
                  <StatusBadge value={u.banned_at ? "banned" : u.is_professional ? "professional" : "active"} />
                </Td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr><Td style={{ textAlign: "center", padding: 32, color: "var(--text-tertiary, #8A8884)" }} colSpan={7}>No users match.</Td></tr>
            )}
          </tbody>
        </table>
      </TableShell>
      <div style={{ fontSize: 12, color: "var(--text-tertiary, #8A8884)" }}>
        Showing {users.length} of {total}
      </div>

      <AddUserModal open={openAdd} onClose={() => setOpenAdd(false)} onCreated={() => { setOpenAdd(false); load(); }} />
      <BatchFitPointsModal open={openBatch} onClose={() => setOpenBatch(false)} />
      {drawerUid && <UserDetailDrawer uid={drawerUid} onClose={() => setDrawerUid(null)} onChanged={load} />}
    </div>
  );
}

function Avatar({ name, url }) {
  return (
    <div style={{
      width: 28, height: 28, borderRadius: "50%",
      background: "var(--bg-inset, #D8D6D1)",
      overflow: "hidden", display: "grid", placeItems: "center",
      fontSize: 11, fontWeight: 700, color: "var(--text-secondary, #4A4845)",
    }}>
      {url ? <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> :
        (name || "?").charAt(0).toUpperCase()}
    </div>
  );
}

function relTime(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const s = Math.max(1, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// ─── Add User ────────────────────────────────────────────────────────
function AddUserModal({ open, onClose, onCreated }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", city: "", goal_type: "", auto_password: true });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.name || !f.email) return toast.error("Name and email required");
    setBusy(true);
    try {
      const { data } = await apiClient.post("/superadmin/users/create", f);
      toast.success(`User created${data.temp_password ? " · pwd: " + data.temp_password : ""}`);
      setF({ name: "", email: "", phone: "", city: "", goal_type: "", auto_password: true });
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create");
    } finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Add User">
      <div style={{ display: "grid", gap: 12 }}>
        <div><label style={fieldLabel}>Full name</label><input style={inputStyle} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="add-user-name" /></div>
        <div><label style={fieldLabel}>Email</label><input style={inputStyle} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="add-user-email" /></div>
        <div><label style={fieldLabel}>Phone</label><input style={inputStyle} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
        <div><label style={fieldLabel}>City</label><input style={inputStyle} value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} /></div>
        <div><label style={fieldLabel}>Goal</label>
          <select style={inputStyle} value={f.goal_type} onChange={(e) => setF({ ...f, goal_type: e.target.value })}>
            <option value="">—</option>
            <option value="lose_weight">Lose Weight</option>
            <option value="gain_muscle">Gain Muscle</option>
            <option value="maintain">Maintain</option>
          </select>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
          <input type="checkbox" checked={f.auto_password} onChange={(e) => setF({ ...f, auto_password: e.target.checked })} />
          Auto-generate password & email credentials
        </label>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
          <GhostBtn onClick={onClose}>Cancel</GhostBtn>
          <PrimaryBtn onClick={submit} disabled={busy} data-testid="add-user-submit">{busy ? "Saving…" : "Create User"}</PrimaryBtn>
        </div>
      </div>
    </Modal>
  );
}

// ─── Batch FitPoints ─────────────────────────────────────────────────
function BatchFitPointsModal({ open, onClose }) {
  const [f, setF] = useState({ target: "all", days_active: 30, city: "", user_ids: "", delta: 25, reason: "" });
  const [preview, setPreview] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const buildBody = () => ({
    target: f.target,
    days_active: parseInt(f.days_active, 10) || 30,
    city: f.city || null,
    user_ids: f.user_ids ? f.user_ids.split(/[\s,]+/).filter(Boolean) : [],
    delta: parseInt(f.delta, 10),
    reason: f.reason,
  });

  useEffect(() => {
    if (!open) return;
    apiClient.post("/superadmin/users/batch-fitpoints/preview", buildBody())
      .then((r) => setPreview(r.data?.recipient_count))
      .catch(() => setPreview(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.target, f.days_active, f.city, f.user_ids, open]);

  const submit = async () => {
    if (confirm !== "CONFIRM") return toast.error("Type CONFIRM to enable");
    if (!f.reason || f.reason.length < 3) return toast.error("Reason required");
    setBusy(true);
    try {
      const { data } = await apiClient.post("/superadmin/users/batch-fitpoints", buildBody());
      toast.success(`Awarded to ${data.recipients} users`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Batch FitPoints">
      <div style={{ display: "grid", gap: 12 }}>
        <div><label style={fieldLabel}>Target</label>
          <select style={inputStyle} value={f.target} onChange={(e) => setF({ ...f, target: e.target.value })} data-testid="batch-target">
            <option value="all">All Users</option>
            <option value="active">Active in last N days</option>
            <option value="city">Users in city</option>
            <option value="custom">Custom user IDs</option>
          </select>
        </div>
        {f.target === "active" && <div><label style={fieldLabel}>Days active</label><input type="number" style={inputStyle} value={f.days_active} onChange={(e) => setF({ ...f, days_active: e.target.value })} /></div>}
        {f.target === "city" && <div><label style={fieldLabel}>City</label><input style={inputStyle} value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} /></div>}
        {f.target === "custom" && <div><label style={fieldLabel}>User IDs (comma or newline separated)</label><textarea style={{ ...inputStyle, height: 90, padding: "10px 14px" }} value={f.user_ids} onChange={(e) => setF({ ...f, user_ids: e.target.value })} /></div>}
        <div><label style={fieldLabel}>Amount (FP)</label><input type="number" style={inputStyle} value={f.delta} onChange={(e) => setF({ ...f, delta: e.target.value })} data-testid="batch-delta" /></div>
        <div><label style={fieldLabel}>Reason (visible in ledger)</label><input style={inputStyle} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="batch-reason" /></div>
        <Card style={{ background: "var(--bg-inset, #D8D6D1)", borderLeft: "3px solid var(--accent, #2A2825)" }}>
          <strong>Estimated recipients:</strong> {preview ?? "—"}
        </Card>
        <div><label style={fieldLabel}>Type CONFIRM to enable</label><input style={inputStyle} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="batch-confirm" /></div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <GhostBtn onClick={onClose}>Cancel</GhostBtn>
          <PrimaryBtn onClick={submit} disabled={busy || confirm !== "CONFIRM"} data-testid="batch-submit">{busy ? "Sending…" : "Send"}</PrimaryBtn>
        </div>
      </div>
    </Modal>
  );
}

// ─── User Detail Drawer ──────────────────────────────────────────────
function UserDetailDrawer({ uid, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("profile");
  const load = useCallback(() => {
    apiClient.get(`/superadmin/users/${uid}`).then((r) => setData(r.data)).catch(() => onClose());
  }, [uid, onClose]);
  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const u = data.user;

  return (
    <div style={{
      position: "fixed", top: 0, right: 0, bottom: 0, width: "min(640px, 100vw)", zIndex: 70,
      background: "var(--bg-surface, #E4E2DE)",
      borderLeft: "1px solid var(--border, rgba(12,12,11,0.09))",
      display: "flex", flexDirection: "column",
    }} data-testid="user-detail-drawer">
      <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border, rgba(12,12,11,0.09))", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontFamily: "Barlow Condensed, sans-serif", fontSize: 22, fontWeight: 800 }}>{u.name}</div>
          <div style={{ fontSize: 12, color: "var(--text-tertiary, #8A8884)" }}>{u.email} · {u.user_id}</div>
        </div>
        <button onClick={onClose} aria-label="Close" data-testid="drawer-close" style={{ background: "transparent" }}><X size={18} /></button>
      </div>
      <div style={{ display: "flex", borderBottom: "1px solid var(--border, rgba(12,12,11,0.09))" }}>
        {["profile", "timeline", "fitpoints", "actions"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`drawer-tab-${t}`}
            style={{ flex: 1, padding: 12, textTransform: "uppercase", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
              borderBottom: tab === t ? "2px solid var(--accent, #2A2825)" : "2px solid transparent",
              background: "transparent", color: tab === t ? "var(--text-primary, #0C0C0B)" : "var(--text-tertiary, #8A8884)" }}>
            {t}
          </button>
        ))}
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
        {tab === "profile" && <ProfileTab user={u} stats={data.stats} onSaved={() => { load(); onChanged(); }} />}
        {tab === "timeline" && <TimelineTab uid={uid} />}
        {tab === "fitpoints" && <FitPointsTab uid={uid} history={data.fitpoints_history} balance={u.fitpoints_balance} onChange={() => { load(); onChanged(); }} />}
        {tab === "actions" && <ActionsTab user={u} onChanged={() => { load(); onChanged(); }} onClose={onClose} />}
      </div>
    </div>
  );
}


// ─── Timeline Tab — merged user activity, newest first ─────────────────
function TimelineTab({ uid }) {
  const [entries, setEntries] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState([]);

  const load = useCallback(async (before) => {
    setLoading(true);
    try {
      const params = { limit: 30 };
      if (before) params.before = before;
      const { data } = await apiClient.get(`/superadmin/users/${uid}/timeline`, { params });
      setEntries((prev) => before ? [...prev, ...data.entries] : data.entries);
      setNextBefore(data.next_before);
      setNotes(data.notes || []);
    } catch {
      /* leave the drawer functional if timeline blows up */
    } finally { setLoading(false); }
  }, [uid]);

  useEffect(() => { load(null); /* first page */ }, [load]);

  return (
    <div data-testid="timeline-tab" className="space-y-3">
      {notes.length > 0 && (
        <div style={{ fontSize: 11, color: "var(--text-tertiary, #8A8884)", lineHeight: 1.5, padding: "8px 10px", background: "var(--bg-inset, #ECEAE7)", borderRadius: 8 }}>
          {notes.join(" · ")}
        </div>
      )}
      {entries.length === 0 && !loading && (
        <div className="py-8 text-center" style={{ color: "var(--text-tertiary, #8A8884)", fontSize: 13 }}>No activity yet for this user.</div>
      )}
      <div className="space-y-1.5">
        {entries.map((e, i) => (
          <TimelineRow key={`${e.kind}-${e.ts}-${i}`} entry={e} />
        ))}
      </div>
      {nextBefore && (
        <button
          onClick={() => load(nextBefore)}
          disabled={loading}
          data-testid="timeline-load-more"
          style={{
            width: "100%", padding: "10px 14px",
            background: "var(--bg-inset, #ECEAE7)", border: 0, borderRadius: 8,
            fontSize: 12, fontWeight: 700, color: "var(--text-primary, #0C0C0B)",
            cursor: loading ? "wait" : "pointer",
          }}
        >
          {loading ? "Loading…" : "Load more"}
        </button>
      )}
    </div>
  );
}

const _KIND_META = {
  workout: { label: "Workout", tint: "#00D4FF" },
  food_log_daily: { label: "Food log", tint: "#F5C518" },
  booking: { label: "Booking", tint: "#0F766E" },
  payment: { label: "Payment", tint: "#0F766E" },
  plan_received: { label: "Plan received", tint: "#7C3AED" },
  fitpoints: { label: "FitPoints", tint: "#F59E0B" },
  arena_checkin: { label: "Arena check-in", tint: "#EF4444" },
  journey_event: { label: "Journey", tint: "#6B7280" },
  report_against: { label: "Report against", tint: "#EF4444" },
  contact_leak_attempt: { label: "Contact leak attempt", tint: "#EF4444" },
  login: { label: "Login", tint: "#0C0C0B" },
};

function TimelineRow({ entry }) {
  const meta = _KIND_META[entry.kind] || { label: entry.kind, tint: "#6B7280" };
  const ts = new Date(entry.ts).toLocaleString("en-IN", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
  let summary = null;
  switch (entry.kind) {
    case "workout":
      summary = `${entry.exercises_count} exercises · ${entry.duration_minutes || "?"} min${entry.calories_burned ? ` · ${entry.calories_burned} kcal` : ""}`;
      break;
    case "food_log_daily":
      summary = `${entry.items_count} items logged · ${entry.total_calories} kcal total`;
      break;
    case "booking":
      summary = `${entry.pro_name} (${entry.pro_type || "trainer"}) · ${entry.booking_kind === "intro" ? "Free intro" : `₹${entry.amount_inr}`} · ${entry.status}`;
      break;
    case "payment":
      summary = `₹${entry.amount_inr} · ${entry.status}${entry.razorpay_order_id ? ` · ${entry.razorpay_order_id}` : ""}`;
      break;
    case "plan_received":
      summary = `${entry.plan_name || entry.plan_type} from ${entry.from_pro || "pro"} · ${entry.viewed ? "viewed" : "unviewed"} · ${entry.status}`;
      break;
    case "fitpoints":
      summary = `${entry.delta > 0 ? "+" : ""}${entry.delta} FP · ${entry.reason || "—"}`;
      break;
    case "arena_checkin":
      summary = `Day ${entry.day_index ?? "?"}${entry.kind ? ` · ${entry.kind}` : ""}`;
      break;
    case "journey_event":
      summary = entry.event_type;
      break;
    case "report_against":
      summary = `${entry.reason} · ${entry.status}`;
      break;
    case "contact_leak_attempt":
      summary = `${entry.reason} · ${entry.count} attempt(s)`;
      break;
    case "login":
      summary = entry.note;
      break;
    default:
      summary = null;
  }
  return (
    <div data-testid={`timeline-row-${entry.kind}`} style={{
      display: "flex", gap: 12, alignItems: "flex-start",
      padding: "10px 12px", background: "white",
      border: "1px solid var(--border, rgba(12,12,11,0.06))",
      borderRadius: 10,
    }}>
      <div style={{
        width: 6, alignSelf: "stretch", background: meta.tint,
        borderRadius: 3, minHeight: 24, flexShrink: 0,
      }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, color: "var(--text-tertiary, #8A8884)" }}>{ts} · {meta.label}</div>
        {summary && (
          <div style={{ fontSize: 13, color: "var(--text-primary, #0C0C0B)", marginTop: 2 }}>{summary}</div>
        )}
      </div>
    </div>
  );
}


function ProfileTab({ user, stats, onSaved }) {
  const [form, setForm] = useState({
    name: user.name || "", phone: user.phone || "", city: user.city || "",
    goal_type: user.goal_type || "", weight_kg: user.weight_kg || "", height_cm: user.height_cm || "",
  });
  const [editing, setEditing] = useState(false);
  const save = async () => {
    try {
      await apiClient.put(`/superadmin/users/${user.user_id}/profile`, {
        ...form,
        weight_kg: form.weight_kg ? parseFloat(form.weight_kg) : null,
        height_cm: form.height_cm ? parseFloat(form.height_cm) : null,
      });
      toast.success("Saved");
      setEditing(false);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };
  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
        <Card><strong>{stats.workouts_total}</strong><br /><span style={{ color: "var(--text-tertiary, #8A8884)", fontSize: 12 }}>workouts</span></Card>
        <Card><strong>{stats.food_scans_total}</strong><br /><span style={{ color: "var(--text-tertiary, #8A8884)", fontSize: 12 }}>food scans</span></Card>
        <Card><strong>{stats.orders_total}</strong><br /><span style={{ color: "var(--text-tertiary, #8A8884)", fontSize: 12 }}>orders</span></Card>
        <Card><strong>₹{stats.total_spend_inr || 0}</strong><br /><span style={{ color: "var(--text-tertiary, #8A8884)", fontSize: 12 }}>total spend</span></Card>
      </div>
      {!editing ? (
        <>
          <Card>
            <Row label="Name" value={user.name} />
            <Row label="Email" value={user.email} />
            <Row label="Phone" value={user.phone || "—"} />
            <Row label="City" value={user.city || "—"} />
            <Row label="Goal" value={user.goal_type || "—"} />
          </Card>
          <PrimaryBtn onClick={() => setEditing(true)} data-testid="edit-profile-btn">Edit Profile</PrimaryBtn>
        </>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          <input style={inputStyle} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" />
          <input style={inputStyle} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" />
          <input style={inputStyle} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City" />
          <select style={inputStyle} value={form.goal_type} onChange={(e) => setForm({ ...form, goal_type: e.target.value })}>
            <option value="">—</option>
            <option value="lose_weight">Lose Weight</option>
            <option value="gain_muscle">Gain Muscle</option>
            <option value="maintain">Maintain</option>
          </select>
          <div style={{ display: "flex", gap: 8 }}>
            <GhostBtn onClick={() => setEditing(false)}>Cancel</GhostBtn>
            <PrimaryBtn onClick={save} data-testid="profile-save">Save</PrimaryBtn>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: 13 }}>
      <span style={{ color: "var(--text-tertiary, #8A8884)" }}>{label}</span>
      <span style={{ color: "var(--text-primary, #0C0C0B)" }}>{value}</span>
    </div>
  );
}

function FitPointsTab({ uid, history, balance, onChange }) {
  const [delta, setDelta] = useState(0);
  const [note, setNote] = useState("");
  const submit = async () => {
    if (!delta || !note) return toast.error("Amount + note required");
    try {
      await apiClient.post(`/superadmin/users/${uid}/fitpoints`, { delta: parseInt(delta, 10), note });
      toast.success("Updated");
      setDelta(0); setNote(""); onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div style={{ display: "grid", gap: 12 }}>
      <Card><strong style={{ fontSize: 24, fontFamily: "Barlow Condensed", fontWeight: 900 }}>{balance ?? 0}</strong> <span style={{ color: "var(--text-tertiary, #8A8884)" }}>FP balance</span></Card>
      <Card>
        <label style={fieldLabel}>Adjust (positive to add, negative to remove)</label>
        <input type="number" style={inputStyle} value={delta} onChange={(e) => setDelta(e.target.value)} data-testid="fp-delta" />
        <div style={{ height: 8 }} />
        <label style={fieldLabel}>Note (visible in ledger)</label>
        <input style={inputStyle} value={note} onChange={(e) => setNote(e.target.value)} data-testid="fp-note" />
        <div style={{ height: 8 }} />
        <PrimaryBtn onClick={submit} data-testid="fp-submit">Apply</PrimaryBtn>
      </Card>
      <Card>
        <div style={fieldLabel}>Recent ledger</div>
        {history.length === 0 && <div style={{ color: "var(--text-tertiary, #8A8884)" }}>No entries.</div>}
        {history.map((h, i) => (
          <div key={i} style={{ padding: "6px 0", fontSize: 13, display: "flex", justifyContent: "space-between" }}>
            <span>{(h.created_at || "").slice(0, 10)} · {h.reason}</span>
            <strong style={{ color: h.delta >= 0 ? "var(--success, #22C55E)" : "var(--error, #EF4444)" }}>{h.delta >= 0 ? "+" : ""}{h.delta}</strong>
          </div>
        ))}
      </Card>
    </div>
  );
}

function ActionsTab({ user, onChanged, onClose }) {
  const impersonate = async (mode) => {
    try {
      const { data } = await apiClient.post(`/superadmin/users/${user.user_id}/impersonate`, { mode });
      // Open the app in a new tab carrying the impersonation token
      const url = `/?imp_token=${encodeURIComponent(data.token)}&imp_mode=${mode}`;
      window.open(url, "_blank");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const ban = async () => {
    const reason = prompt("Reason for ban:");
    if (!reason) return;
    try {
      await apiClient.post(`/superadmin/users/${user.user_id}/ban`, { reason });
      toast.success("User banned");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const unban = async () => {
    await apiClient.post(`/superadmin/users/${user.user_id}/unban`);
    toast.success("User unbanned"); onChanged();
  };
  const del = async () => {
    const c = prompt("Type DELETE to confirm");
    if (c !== "DELETE") return;
    await apiClient.post(`/superadmin/users/${user.user_id}/delete`);
    toast.success("User soft-deleted");
    onClose();
  };
  return (
    <div style={{ display: "grid", gap: 10 }}>
      <PrimaryBtn onClick={() => impersonate("read")} data-testid="impersonate-read">Impersonate (Read-only)</PrimaryBtn>
      <GhostBtn onClick={() => impersonate("write")} data-testid="impersonate-write">Impersonate (Write mode)</GhostBtn>
      {user.banned_at ? (
        <PrimaryBtn onClick={unban} data-testid="unban-user">Unban User</PrimaryBtn>
      ) : (
        <DangerBtn onClick={ban} data-testid="ban-user">Ban User</DangerBtn>
      )}
      <DangerBtn onClick={del} data-testid="delete-user">Delete User</DangerBtn>
    </div>
  );
}
